import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Input, InputRef, Spin } from 'antd';
import Modal from 'components/Modal';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import { InputUIV2 } from 'widgets/RequestAdminLocalPW';
import { PIN_LENGTH } from 'components/NumberKeyBoard';
import { debounce } from 'lodash';
import { IStaffItemData } from 'services/shop/types/staff';
import { IResponseDataBody } from 'services/response';
import shopApis from 'services/shop/apis';

export type PinCodeModalRef = {
  open: (callback: (data: IStaffItemData) => void) => void;
  close: () => void;
};

type PinCodeModalProps = {
  title: string;
};

const PinCodeModal = forwardRef<PinCodeModalRef, PinCodeModalProps>(({ title }, ref) => {
  const callbackRef = useRef<((data: IStaffItemData) => void) | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [visible, setVisible] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const handleClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setVisible(false);
  };

  const _onChange = (text: string) => {
    if (text.length > PIN_LENGTH) return;
    setPin(text);
  };

  useImperativeHandle(ref, () => ({
    open: (callback) => {
      callbackRef.current = callback;
      setVisible(true);
      setPin('');
      setError(undefined);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    },
    close: () => handleClose(),
  }));

  const handleCheckPinCode = async (pin: string) => {
    setLoading(true);
    try {
      const res: IResponseDataBody<IStaffItemData> = await shopApis.verifyNormalPin(pin);
      if (res.data.data) {
        callbackRef.current?.(res.data.data);
        handleClose();
      } else {
        setError('Password incorrect!');
      }
    } catch (error) {
      setError('Password incorrect!');
    } finally {
      setLoading(false);
    }
  };

  const _debounce = useCallback(
    debounce((_pin) => handleCheckPinCode(_pin), 500),
    [pin]
  );

  useEffect(() => {
    if (visible && pin.length === PIN_LENGTH) {
      _debounce(pin);
    }
  }, [visible, pin]);

  return (
    <Modal
      modalTitle={title}
      hiddenOkButton
      v2
      noneBodyStyle
      width={'800px'}
      visible={visible}
      onClose={handleClose}
    >
      <div onClick={(e) => e.stopPropagation()}>
        <Spin spinning={loading}>
          <InputUIV2>
            <Input
              ref={inputRef}
              name='input_password'
              value={pin}
              autoFocus
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              inputMode='none'
              className={'password-input'}
              onChange={(e) => {
                _onChange(e.target.value);
              }}
              maxLength={PIN_LENGTH}
              autoComplete='off'
            />
          </InputUIV2>
          {error && (
            <Text textAlign='center' mt={0.5} style={{ color: '#f53f3f', fontSize: 18 }}>
              {error}
            </Text>
          )}
          <NumberPadV2 value={pin} onChange={(val) => _onChange(val || '')} type='PIN-PASSWORD' />
        </Spin>
      </div>
    </Modal>
  );
});
PinCodeModal.displayName = 'PinCodeModal';

export default PinCodeModal;
